import React, { useState, useEffect, ChangeEvent } from 'react';
import { useNavigate } from '../i18n/router';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Modal,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Container,
  Card,
  CardContent,
  Fade,
  Chip,
  InputAdornment,
  Paper,
  Backdrop,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { SelectChangeEvent } from '@mui/material/Select';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import BadgeIcon from '@mui/icons-material/Badge';
import HomeIcon from '@mui/icons-material/Home';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkIcon from '@mui/icons-material/Work';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import EditIcon from '@mui/icons-material/Edit';
import { useLocation } from 'react-router-dom';

import modifieVoyageur from '../modifieVoyageurService';
import { Voyageurs } from '../models/Voyageurs';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(3),
  border: '1px solid',
  borderColor: theme.palette.divider,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.12)',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
    },
  },
}));

const StyledSelect = styled(Select<string>)(({ theme }) => ({
  borderRadius: 12,
  backgroundColor: theme.palette.background.paper,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.Mui-focused': {
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
  },
})) as unknown as typeof Select<string>;

const UploadButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(2),
  borderStyle: 'dashed',
  borderWidth: 2,
  backgroundColor: theme.palette.background.paper,
  transition: 'all 0.3s ease',
  '&:hover': {
    borderStyle: 'solid',
    backgroundColor: theme.palette.action.hover,
  },
})) as typeof Button;

const PreviewContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  borderRadius: 12,
  overflow: 'hidden',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
  position: 'relative',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

// ----------------------------------------------------------------
// Composant DocumentOrPhotoPicker (modernisé)
// ----------------------------------------------------------------

interface DocumentOrPhotoPickerProps {
  documentType: string;
  onDocumentSelected: (uri: string) => void;
  modeOptions: string[];
  icon: React.ReactNode;
  currentUri?: string | null;
}

const DocumentOrPhotoPicker: React.FC<DocumentOrPhotoPickerProps> = ({
  documentType,
  onDocumentSelected,
  modeOptions,
  icon,
  currentUri,
}) => {
  const { t } = useTranslation('voyageur');
  const [preview, setPreview] = useState<string | null>(currentUri || null);
  const [fileType, setFileType] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const acceptedTypes = modeOptions.includes('selectPdf')
    ? 'image/*,application/pdf'
    : 'image/*';

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const fileURL = URL.createObjectURL(file);
      setPreview(fileURL);
      setFileType(file.type);
      setFileName(file.name);
      onDocumentSelected(fileURL);
    }
  };

  return (
    <Box>
      <UploadButton
        variant="outlined"
        component="label"
        fullWidth
        startIcon={<CloudUploadIcon />}
        size="large"
      >
        {icon}
        <Box ml={1}>
          {preview ? t('documents.modify', { document: documentType }) : t('documents.upload', { document: documentType })}
        </Box>
        <input type="file" hidden accept={acceptedTypes} onChange={handleFileChange} />
      </UploadButton>

      {preview && (
        <Fade in={true}>
          <PreviewContainer>
            {fileName && (
              <Box p={2} bgcolor="background.default">
                <Chip
                  icon={fileType === 'application/pdf' ? <PictureAsPdfIcon /> : <ImageIcon />}
                  label={fileName}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              </Box>
            )}
            {fileType === 'application/pdf' || (preview && preview.includes('.pdf')) ? (
              <Box sx={{ width: '100%', height: 400 }}>
                <iframe
                  src={preview}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="aperçu pdf"
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: 300,
                  backgroundImage: `url(${preview})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundColor: 'grey.100',
                }}
              />
            )}
          </PreviewContainer>
        </Fade>
      )}
    </Box>
  );
};

// ----------------------------------------------------------------
// Interface pour récupérer le voyageur depuis l'état de la route
// ----------------------------------------------------------------

interface LocationState {
  voyageur: Voyageurs;
}

// ----------------------------------------------------------------
// Composant principal VoyageurUpdate
// ----------------------------------------------------------------

const VoyageurUpdate: React.FC = () => {
  const { t } = useTranslation('voyageur');
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | undefined;
  const initialVoyageur = state?.voyageur || null;

  const [voyageur, setVoyageur] = useState<Voyageurs | null>(initialVoyageur);

  // États de validation
  const [nomError, setNomError] = useState(false);
  const [prenomError, setPrenomError] = useState(false);
  const [telephoneError, setTelephoneError] = useState(false);
  const [professionError, setProfessionError] = useState(false);
  const [etatCivilError, setEtatCivilError] = useState(false);

  // États pour la gestion de l'adresse
  const [adresse, setAdresse] = useState(voyageur?.adresse || '');
  const [codePostal, setCodePostal] = useState(voyageur?.cp || '');
  const [ville, setVille] = useState(voyageur?.ville || '');

  // États pour les photos/documents
  const [selectedPhotoUri, setSelectedPhotoUri] = useState<string | null>(
    voyageur?.url || null
  );
  const defaultPasseportUri = voyageur?.url
    ? voyageur.url.replace('photo.jpg', 'passeport.jpg')
    : '';
  const [selectedPasseportUri, setSelectedPasseportUri] = useState<string | null>(
    defaultPasseportUri || null
  );

  // État civil
  const [etatCivil, setEtatCivil] = useState(voyageur?.etat_civil || '');

  // État de chargement
  const [isLoading, setIsLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  // États pour le Modal d'aperçu d'image
  const [modalVisible, setModalVisible] = useState(false);
  const [previewImageUri, setPreviewImageUri] = useState<string | null>(null);

  // Redirection si aucun voyageur n'est disponible
  useEffect(() => {
    if (!voyageur) {
      navigate(-1);
    }
  }, [voyageur, navigate]);

  // Validation du formulaire à chaque changement
  useEffect(() => {
    const isValid = validateForm();
    setIsFormValid(isValid);
  }, [
    voyageur?.nom,
    voyageur?.prenom,
    voyageur?.telephone,
    voyageur?.profession,
    etatCivil,
    adresse,
    codePostal,
    ville,
  ]);

  // Validation du formulaire
  const validateForm = (): boolean => {
    let isValid = true;

    if (!voyageur?.nom.trim()) {
      setNomError(true);
      isValid = false;
    } else {
      setNomError(false);
    }

    if (!voyageur?.prenom.trim()) {
      setPrenomError(true);
      isValid = false;
    } else {
      setPrenomError(false);
    }

    if (!etatCivil.trim()) {
      setEtatCivilError(true);
      isValid = false;
    } else {
      setEtatCivilError(false);
    }

    const phoneRegex = /^\d+$/;
    if (!voyageur?.telephone.trim() || !phoneRegex.test(voyageur.telephone.trim())) {
      setTelephoneError(true);
      isValid = false;
    } else {
      setTelephoneError(false);
    }

    if (!voyageur?.profession.trim()) {
      setProfessionError(true);
      isValid = false;
    } else {
      setProfessionError(false);
    }

    if (!adresse.trim() || !codePostal.trim() || !ville.trim()) {
      isValid = false;
    }
    return isValid;
  };

  // Soumission du formulaire
  const handleSubmit = async () => {
    if (isFormValid && voyageur) {
      setIsLoading(true);
      try {
        const newPhotoUri =
          selectedPhotoUri !== voyageur.url ? selectedPhotoUri : null;
        const newPasseportUri =
          selectedPasseportUri !== defaultPasseportUri ? selectedPasseportUri : null;

        await modifieVoyageur(
          voyageur.id,
          voyageur.nom,
          voyageur.prenom,
          newPhotoUri,
          newPasseportUri,
          adresse,
          codePostal,
          ville,
          voyageur.telephone,
          voyageur.profession,
          etatCivil
        );
        setIsLoading(false);
        navigate(-1);
      } catch (error) {
        console.error('Erreur lors de la mise à jour :', error);
        setIsLoading(false);
      }
    }
  };

  // Gestion de l'ouverture/fermeture du Modal
  const handleOpenModal = (uri: string) => {
    setPreviewImageUri(uri);
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setPreviewImageUri(null);
  };

  // Mise à jour des champs du voyageur
  const handleChangeVoyageurField = (field: keyof Voyageurs, value: string) => {
    setVoyageur((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  // Filtrage du téléphone
  const handleTelephoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const filteredText = e.target.value.replace(/[^0-9]/g, '');
    handleChangeVoyageurField('telephone', filteredText);
  };

  return (
    <>
      {voyageur ? (
        <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh', py: { xs: 2, sm: 3 } }}>
          <Container maxWidth="lg">
            {/* Header */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                mb: { xs: 3, sm: 4 },
                borderRadius: 3,
                background: 'linear-gradient(135deg, #D89D2F 0%, #B5811F 100%)',
                color: 'white',
              }}
            >
              <Box display="flex" alignItems="center">
                <IconButton
                  onClick={() => navigate(-1)}
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Box ml={2}>
                  <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.4rem', sm: '2rem' } }}>
                    {t('update.title')}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ opacity: 0.9, mt: 0.5, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                    {t('update.subtitle', { prenom: voyageur.prenom, nom: voyageur.nom })}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Backdrop
              sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={isLoading}
            >
              <CircularProgress color="inherit" size={60} />
            </Backdrop>

            <Grid container spacing={3}>
              {/* Informations personnelles */}
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent sx={{ p: 4 }}>
                    <SectionTitle variant="h6">
                      <PersonIcon color="primary" />
                      {t('sections.personalInfo')}
                    </SectionTitle>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <StyledTextField
                          fullWidth
                          label={t('fields.lastName')}
                          value={voyageur.nom}
                          error={nomError}
                          helperText={nomError ? t('errors.required') : ''}
                          onChange={(e) => handleChangeVoyageurField('nom', e.target.value)}
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <StyledTextField
                          fullWidth
                          label={t('fields.firstName')}
                          value={voyageur.prenom}
                          error={prenomError}
                          helperText={prenomError ? t('errors.required') : ''}
                          onChange={(e) => handleChangeVoyageurField('prenom', e.target.value)}
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth error={etatCivilError}>
                          <InputLabel id="etat-civil-label">{t('fields.etatCivil')}</InputLabel>
                          <StyledSelect
                            labelId="etat-civil-label"
                            value={etatCivil}
                            label={t('fields.etatCivil')}
                            onChange={(e) => setEtatCivil(e.target.value as string)}
                            startAdornment={
                              <InputAdornment position="start">
                                <FavoriteIcon fontSize="small" />
                              </InputAdornment>
                            }
                          >
                            <MenuItem value="célibataire">{t('etatCivilOptions.single')}</MenuItem>
                            <MenuItem value="marié">{t('etatCivilOptions.married')}</MenuItem>
                            <MenuItem value="divorcé">{t('etatCivilOptions.divorced')}</MenuItem>
                            <MenuItem value="veuf">{t('etatCivilOptions.widowed')}</MenuItem>
                          </StyledSelect>
                          {etatCivilError && (
                            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                              {t('errors.etatCivilRequired')}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    </Grid>
                  </CardContent>
                </StyledCard>

                {/* Contact et profession */}
                <StyledCard>
                  <CardContent sx={{ p: 4 }}>
                    <SectionTitle variant="h6">
                      <PhoneIcon color="primary" />
                      {t('sections.contactProfession')}
                    </SectionTitle>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <StyledTextField
                          fullWidth
                          label={t('fields.phone')}
                          value={voyageur.telephone}
                          error={telephoneError}
                          helperText={
                            telephoneError
                              ? t('errors.invalidPhone')
                              : ''
                          }
                          onChange={handleTelephoneChange}
                          variant="outlined"
                          inputProps={{ inputMode: 'numeric' }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <StyledTextField
                          fullWidth
                          label={t('fields.profession')}
                          value={voyageur.profession}
                          error={professionError}
                          helperText={professionError ? t('errors.required') : ''}
                          onChange={(e) => handleChangeVoyageurField('profession', e.target.value)}
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <WorkIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </StyledCard>

                {/* Adresse */}
                <StyledCard>
                  <CardContent sx={{ p: 4 }}>
                    <SectionTitle variant="h6">
                      <HomeIcon color="primary" />
                      {t('sections.address')}
                    </SectionTitle>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <StyledTextField
                          fullWidth
                          required
                          label={t('fields.address')}
                          value={adresse}
                          onChange={(e) => setAdresse(e.target.value)}
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <HomeIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <StyledTextField
                          fullWidth
                          required
                          label={t('fields.postalCode')}
                          value={codePostal}
                          onChange={(e) => setCodePostal(e.target.value)}
                          variant="outlined"
                          type="tel"  // Ajouter cette ligne
                          inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*'  // Ajouter pour iOS
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <StyledTextField
                          fullWidth
                          required
                          label={t('fields.city')}
                          value={ville}
                          onChange={(e) => setVille(e.target.value)}
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </StyledCard>
              </Grid>

              {/* Documents */}
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent sx={{ p: 4 }}>
                    <SectionTitle variant="h6">
                      <PhotoCameraIcon color="primary" />
                      {t('sections.documents')}
                    </SectionTitle>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <DocumentOrPhotoPicker
                          documentType={t('documents.photo')}
                          onDocumentSelected={setSelectedPhotoUri}
                          modeOptions={['photo', 'selectPhoto']}
                          icon={<PhotoCameraIcon />}
                          currentUri={selectedPhotoUri}
                        />
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 2, border: '1px solid', borderColor: 'info.main' }}>
                          <Typography variant="caption" color="info.main" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                            {t('documents.photoExample')}
                          </Typography>
                          <Box
                            component="img"
                            src="/photoexemple.jpg"
                            alt={t('documents.photoExample')}
                            sx={{
                              width: '100%',
                              maxWidth: 200,
                              height: 'auto',
                              borderRadius: 1,
                              boxShadow: 2,
                              display: 'block',
                              mx: 'auto'
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                            {t('documents.photoExampleCaption')}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <DocumentOrPhotoPicker
                          documentType={t('documents.passport')}
                          onDocumentSelected={setSelectedPasseportUri}
                          modeOptions={['photo', 'selectPhoto', 'selectPdf']}
                          icon={<BadgeIcon />}
                          currentUri={selectedPasseportUri}
                        />
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 2, border: '1px solid', borderColor: 'info.main' }}>
                          <Typography variant="caption" color="info.main" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                            {t('documents.passportExample')}
                          </Typography>
                          <Box
                            component="img"
                            src="/paxexemple.jpg"
                            alt={t('documents.passportExample')}
                            sx={{
                              width: '100%',
                              maxWidth: 300,
                              height: 'auto',
                              borderRadius: 1,
                              boxShadow: 2,
                              display: 'block',
                              mx: 'auto'
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                            {t('documents.passportExampleCaption')}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </StyledCard>
              </Grid>

              {/* Bouton de soumission */}
              <Grid item xs={12}>
                <Box sx={{ mt: 2, mb: 3, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    size="large"
                    disabled={!isFormValid}
                    onClick={handleSubmit}
                    startIcon={<EditIcon />}
                    sx={{
                      borderRadius: 3,
                      py: 1.5,
                      px: { xs: 4, sm: 6 },
                      width: { xs: '100%', sm: 'auto' },
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      textTransform: 'none',
                      fontWeight: 600,
                      background: isFormValid
                        ? 'linear-gradient(135deg, #D89D2F 0%, #B5811F 100%)'
                        : undefined,
                      boxShadow: isFormValid ? '0 4px 14px rgba(46,125,50,0.3)' : undefined,
                      '&:hover': {
                        background: isFormValid
                          ? 'linear-gradient(135deg, #B5811F 0%, #8C6418 100%)'
                          : undefined,
                        boxShadow: isFormValid ? '0 6px 20px rgba(46,125,50,0.4)' : undefined,
                      },
                    }}
                  >
                    {t('update.title')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
      ) : null}

      {/* Modal d'aperçu d'image */}
      <Modal open={modalVisible} onClose={handleCloseModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 2,
            maxWidth: '90vw',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': {
                bgcolor: 'background.default',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {previewImageUri && (
            <Box
              component="img"
              src={previewImageUri}
              alt="aperçu"
              sx={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                mt: 4,
              }}
            />
          )}
        </Box>
      </Modal>
    </>
  );
};

export default VoyageurUpdate;